import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link } from 'react-router-dom';
import { FaPlay, FaTimes, FaTrashAlt } from 'react-icons/fa';
import profileClipMark from './profileClipMark.jpg';
import { buildVideoMomentPath, formatVideoTime } from './videoMoment';
import { clipPauseDecision, replayClipFromStart } from './clipShare';
import { attachYoutubeEmbed, YT_STATE } from './youtubeEmbedPlayer';

const NEAR_BUFFER = 2;

const formatTimeAgo = (dateString) => {
  const postDate = new Date(dateString);
  const diffMs = Date.now() - postDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffMinutes < 60) return `${Math.max(0, diffMinutes)}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 30) return `${diffDays}d`;
  return postDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const byNewest = (a, b) => new Date(b.createdAt) - new Date(a.createdAt);

export function clipYoutubeId(clip) {
  const direct = String(clip?.youtubeVideoId || '').trim();
  if (direct) return direct;
  const url = String(clip?.url || '');
  const match = url.match(/(?:embed\/|v=|youtu\.be\/|\/live\/|shorts\/)([A-Za-z0-9_-]{6,32})/);
  return match ? match[1] : '';
}

export function clipThumbnailUrl(clip) {
  const id = clipYoutubeId(clip);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '';
}

export function clipPlayerSrc(clip, { autoplay = true } = {}) {
  const id = clipYoutubeId(clip);
  if (!id) return '';
  const start = Math.max(0, Math.floor(Number(clip.startSeconds) || 0));
  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    enablejsapi: '1',
    start: String(start),
    rel: '0',
    modestbranding: '1',
    playsinline: '1',
    controls: '0',
    iv_load_policy: '3',
    fs: '0'
  });
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube.com/embed/${id}?${params.toString()}`;
}

function clipStartSeconds(clip) {
  return Math.max(0, Math.floor(Number(clip?.startSeconds) || 0));
}

function clipEndSeconds(clip) {
  const start = clipStartSeconds(clip);
  return Math.max(start, Math.floor(Number(clip?.endSeconds) || 0));
}

const CLIP_EDGE = 0.15;

export function clipTapZone(xRatio) {
  if (xRatio < CLIP_EDGE) return 'left';
  if (xRatio > 1 - CLIP_EDGE) return 'right';
  return 'center';
}

/** Center tap pauses. A second tap on an edge within 300ms seeks ±10. One edge tap does not. */
export function clipStageTap({ zone, now, lastTap, doubleMs = 300 }) {
  if (zone === 'center') return { action: 'toggle', lastTap: { time: 0, zone: null } };
  if (lastTap && lastTap.zone === zone && now - lastTap.time < doubleMs) {
    return { action: 'seek', delta: zone === 'left' ? -10 : 10, lastTap: { time: 0, zone: null } };
  }
  return { action: 'chrome', lastTap: { time: now, zone } };
}

function viewportIsPortrait() {
  if (typeof window === 'undefined') return true;
  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(orientation: portrait)').matches;
  }
  return window.innerHeight >= window.innerWidth;
}

function requestClipFullscreen(node) {
  if (!node) return;
  if (document.fullscreenElement || document.webkitFullscreenElement) return;
  const request = node.requestFullscreen || node.webkitRequestFullscreen;
  if (!request) return;
  try {
    const pending = request.call(node, { navigationUI: 'hide' });
    if (pending && typeof pending.catch === 'function') pending.catch(() => {});
  } catch (_) {}
}

function exitClipFullscreen() {
  const active = document.fullscreenElement || document.webkitFullscreenElement;
  if (!active) return;
  const exit = document.exitFullscreen || document.webkitExitFullscreen;
  try {
    const pending = exit && exit.call(document);
    if (pending && typeof pending.catch === 'function') pending.catch(() => {});
  } catch (_) {}
}

/** Swipe up moves forward through the creator's list. Swipe down moves back. */
export function nextClipInList(clips, current, direction) {
  if (!current || !Array.isArray(clips) || !clips.length) return null;
  const index = clips.findIndex((clip) => clip.clipId === current.clipId);
  if (index < 0) return null;
  const next = index + direction;
  if (next < 0 || next >= clips.length) return null;
  return clips[next];
}

/** Indexes whose still image may load: every visible clip, plus `buffer` on each side. */
export function clipAssetWindow(visibleIndexes, count, buffer = NEAR_BUFFER) {
  const visible = [...visibleIndexes].filter((index) => index >= 0 && index < count);
  if (!visible.length || count < 1) return [];
  const min = Math.min(...visible);
  const max = Math.max(...visible);
  const start = Math.max(0, min - buffer);
  const end = Math.min(count - 1, max + buffer);
  const indexes = [];
  for (let index = start; index <= end; index += 1) indexes.push(index);
  return indexes;
}

function useNearClipIndexes(count) {
  const [visible, setVisible] = useState(() => []);
  const nodes = useRef(new Map());
  const observer = useRef(null);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'function') return undefined;
    const obs = new IntersectionObserver((entries) => {
      setVisible((current) => {
        const next = new Set(current);
        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute('data-clip-index'));
          if (!Number.isFinite(index)) return;
          if (entry.isIntersecting) next.add(index);
          else next.delete(index);
        });
        return [...next];
      });
    }, { root: null, threshold: 0.01 });
    observer.current = obs;
    nodes.current.forEach((node) => obs.observe(node));
    return () => {
      obs.disconnect();
      observer.current = null;
    };
  }, []);

  useEffect(() => {
    setVisible([]);
  }, [count]);

  const refFor = (index) => (node) => {
    const previous = nodes.current.get(index);
    if (previous && previous !== node && observer.current) observer.current.unobserve(previous);
    if (node) {
      node.setAttribute('data-clip-index', String(index));
      nodes.current.set(index, node);
      observer.current?.observe(node);
    } else {
      nodes.current.delete(index);
    }
  };

  const near = useMemo(() => {
    if (typeof IntersectionObserver !== 'function') {
      return clipAssetWindow([0], count, count);
    }
    return clipAssetWindow(visible, count, NEAR_BUFFER);
  }, [visible, count]);

  return { refFor, isNear: (index) => near.includes(index) };
}

export function ProfileMediaSwitch({ tab, onChange }) {
  const half = (id, label) => {
    const selected = tab === id;
    return (
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => onChange(id)}
        className={`px-4 py-1.5 text-sm font-semibold rounded-full ${
          selected ? 'bg-gray-900 text-white' : 'text-gray-700 hover:text-gray-900'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      className="inline-flex rounded-full bg-gray-200 p-0.5 mb-4"
      role="group"
      aria-label="Clips and videos"
    >
      {half('clips', 'Clips')}
      {half('videos', 'Videos')}
    </div>
  );
}

const LANDSCAPE_META_MS = 1000;

function useIsPortrait() {
  const [portrait, setPortrait] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (typeof window.matchMedia === 'function') {
      return window.matchMedia('(orientation: portrait)').matches;
    }
    return window.innerHeight >= window.innerWidth;
  });

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(orientation: portrait)');
    const update = () => setPortrait(media.matches);
    update();
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, []);

  return portrait;
}

function ClipPlayMeta({ clip }) {
  const when = clip.createdAt ? formatTimeAgo(clip.createdAt) : '';
  const hall = clip.hallDisplayName || '';
  return (
    <div>
      <p className="font-semibold truncate">{clip.title || 'Clip'}</p>
      {(when || hall) ? (
        <p className="text-sm text-gray-200">
          {when}
          {when && hall ? ' · ' : ''}
          {hall}
        </p>
      ) : null}
    </div>
  );
}

function ClipPlayerFrame({
  playing,
  slotRef,
  shellRef,
  portrait,
  showLandscapeMeta,
  onClose,
  onSwipe,
  onCovered,
  canSwipe,
  onTransport
}) {
  const stageRef = useRef(null);
  const videoWrapRef = useRef(null);
  const dragDirRef = useRef(0);
  const phaseRef = useRef('idle');
  const [portraitFrame, setPortraitFrame] = useState({ top: 0, height: 0, paddingTop: 0 });
  const [dragY, setDragY] = useState(0);
  const [phase, setPhase] = useState('idle');
  const [stageHeight, setStageHeight] = useState(0);
  const gestureRef = useRef({ canSwipe, onSwipe, onTransport, portrait });
  gestureRef.current = { canSwipe, onSwipe, onTransport, portrait };
  phaseRef.current = phase;

  useEffect(() => {
    if (!playing) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [playing, onClose]);

  useEffect(() => {
    if (phase !== 'cover') return undefined;
    const timer = setTimeout(() => setPhase('reveal'), 280);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'reveal') return undefined;
    const timer = setTimeout(() => setPhase('idle'), 480);
    return () => clearTimeout(timer);
  }, [phase]);

  useEffect(() => {
    if (!playing) {
      setDragY(0);
      setPhase('idle');
    }
  }, [playing]);

  useEffect(() => {
    if (!playing || !portrait) return undefined;
    const measure = () => {
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      const top = viewport?.offsetTop || 0;
      const header = document.querySelector('header');
      const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      const paddingTop = headerBottom > 0 && headerBottom < height ? Math.round(headerBottom) : 0;
      setPortraitFrame({ top, height, paddingTop });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    window.visualViewport?.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.visualViewport?.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('scroll', measure);
    };
  }, [playing, portrait]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!playing || !stage) return undefined;
    let track = null;

    const inside = (event) => {
      const rect = stage.getBoundingClientRect();
      return event.clientX >= rect.left && event.clientX <= rect.right
        && event.clientY >= rect.top && event.clientY <= rect.bottom
        ? rect
        : null;
    };

    const onDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      if (phaseRef.current === 'settle' || phaseRef.current === 'cover' || phaseRef.current === 'reveal') return;
      if (event.target?.closest?.('button')) return;
      const rect = inside(event);
      if (!rect) return;
      const ratio = rect.width ? (event.clientX - rect.left) / rect.width : 0.5;
      track = {
        x: event.clientX,
        y: event.clientY,
        dy: 0,
        drag: false,
        zone: clipTapZone(ratio),
        pointerId: event.pointerId,
        height: rect.height
      };
    };

    const onMove = (event) => {
      if (!track || event.pointerId !== track.pointerId) return;
      const dy = event.clientY - track.y;
      const dx = Math.abs(event.clientX - track.x);
      track.dy = dy;
      if (!track.drag && Math.abs(dy) < 10) return;
      if (!track.drag && dx > Math.abs(dy)) return;
      track.drag = true;
      setStageHeight(track.height);
      if (track.zone !== 'center' && event.cancelable) event.preventDefault();
      try { stage.setPointerCapture(event.pointerId); } catch (_) {}
      const direction = dy < 0 ? 1 : -1;
      if (!gestureRef.current.canSwipe(direction)) {
        dragDirRef.current = 0;
        setDragY(dy * 0.2);
        return;
      }
      dragDirRef.current = direction;
      setPhase('drag');
      setDragY(dy);
    };

    const onUp = (event) => {
      if (!track || event.pointerId !== track.pointerId) return;
      const finished = track;
      track = null;
      if (!gestureRef.current.portrait) requestClipFullscreen(shellRef.current);
      if (!finished.drag) {
        if (finished.zone !== 'center') return;
        gestureRef.current.onTransport('toggle');
        return;
      }
      const height = finished.height || stage.getBoundingClientRect().height || 1;
      const passed = Math.abs(finished.dy) > Math.max(64, height * 0.16);
      const direction = dragDirRef.current;
      if (!passed || !direction || !gestureRef.current.canSwipe(direction)) {
        setPhase('settle');
        setDragY(0);
        window.setTimeout(() => setPhase('idle'), 260);
        return;
      }
      if (event.cancelable) event.preventDefault();
      gestureRef.current.onSwipe(direction);
      setPhase('settle');
      setDragY(direction > 0 ? -height : height);
    };

    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerup', onUp, true);
    window.addEventListener('pointercancel', onUp, true);
    return () => {
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerup', onUp, true);
      window.removeEventListener('pointercancel', onUp, true);
    };
  }, [playing]);

  const onTransitionEnd = (event) => {
    if (event.propertyName !== 'transform' || phase !== 'settle') return;
    if (Math.abs(dragY) < 40) return;
    if (phaseRef.current === 'cover' || phaseRef.current === 'reveal') return;
    setPhase('cover');
    setDragY(0);
    onCovered?.();
  };

  const motion = phase === 'settle' ? 'transform 280ms cubic-bezier(.22,.85,.3,1)' : 'none';
  const logoTravel = dragDirRef.current > 0 ? stageHeight : -stageHeight;
  const logoParked = phase === 'cover' || phase === 'reveal' || phase === 'idle';
  const logoY = logoParked ? 0 : dragY + logoTravel;
  const logoVisible = phase === 'cover' || ((phase === 'drag' || phase === 'settle') && dragDirRef.current !== 0);
  const shellClass = !playing
    ? 'fixed left-[-120vw] top-0 h-[56.25vw] w-screen overflow-hidden pointer-events-none'
    : portrait
      ? 'fixed left-0 right-0 z-50 flex items-center justify-center overflow-hidden bg-black'
      : 'fixed inset-0 z-50 bg-black';
  const stageClass = !playing
    ? 'relative h-full w-full overflow-hidden bg-black'
    : portrait
      ? 'relative w-full overflow-hidden bg-black'
      : 'absolute inset-0 overflow-hidden bg-black';

  return (
    <div
      ref={shellRef}
      className={shellClass}
      style={playing && portrait ? {
        top: portraitFrame.top,
        bottom: 'auto',
        height: portraitFrame.height || '100dvh',
        paddingTop: portraitFrame.paddingTop
      } : undefined}
      role={playing ? 'dialog' : undefined}
      aria-modal={playing ? 'true' : undefined}
      aria-label={playing ? (playing.title || 'Clip') : undefined}
    >
      <div className={playing && portrait ? 'flex h-full w-full min-h-0 flex-col items-center justify-center overflow-hidden' : 'relative h-full w-full'}>
        <div
          ref={stageRef}
          className={stageClass}
          style={playing && portrait ? { aspectRatio: '16 / 9', maxHeight: '100%', width: '100%' } : undefined}
        >
            <div
              ref={videoWrapRef}
              className="absolute inset-0 pointer-events-none"
              style={{
                transform: `translateY(${phase === 'cover' || phase === 'reveal' ? 0 : dragY}px)`,
                transition: motion
              }}
            onTransitionEnd={onTransitionEnd}
          >
            <div ref={slotRef} className="absolute inset-0" />
          </div>
          <img
            src={profileClipMark}
            alt=""
            className="absolute inset-0 h-full w-full object-cover pointer-events-none"
            style={{
                transform: `translateY(${logoY}px)`,
                transition: phase === 'reveal'
                  ? 'opacity 450ms ease'
                  : motion,
                opacity: playing && logoVisible && phase !== 'reveal' ? 1 : 0
            }}
            draggable={false}
          />
          {playing ? (
            <div
              className="absolute top-0 bottom-0 z-10"
              style={{ left: '15%', width: '70%', touchAction: 'none' }}
            />
          ) : null}
        </div>
        {playing && portrait ? (
          <div className="px-4 py-3 text-white">
            <ClipPlayMeta clip={playing} />
          </div>
        ) : null}
        {playing && !portrait && showLandscapeMeta ? (
          <div className="absolute bottom-0 left-0 right-0 z-20 px-4 py-3 text-white bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
            <ClipPlayMeta clip={playing} />
          </div>
        ) : null}
        {playing ? (
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 z-30 text-white bg-black/60 rounded-full w-10 h-10 flex items-center justify-center"
            aria-label="Close"
          >
            <FaTimes />
          </button>
        ) : null}
      </div>
    </div>
  );
}

function ClipCard({ clip, isAppMode, canRemove, onRemove, archived, onUnarchive, loadMedia, onPlay, cardRef }) {
  const start = Math.max(0, Math.floor(Number(clip.startSeconds) || 0));
  const end = Math.max(start, Math.floor(Number(clip.endSeconds) || 0));
  const momentPath = buildVideoMomentPath(clip.videoId, start, { forceT: true });
  const videoHref = isAppMode ? momentPath : `/legacy${momentPath}`;
  const userHref = (name) => `${isAppMode ? '/user' : '/legacy/user'}/${encodeURIComponent(name)}`;
  const creator = clip.videoSubmitter || '';
  const submitter = clip.submitter || '';
  const thumbnail = loadMedia ? clipThumbnailUrl(clip) : '';

  return (
    <article ref={cardRef} className={`border rounded-lg overflow-hidden ${archived ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}>
      <div className="relative pb-[56.25%] h-0 bg-gray-300">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}
        <button
          type="button"
          className="absolute inset-0 flex items-center justify-center"
          aria-label={`Play ${clip.title || 'clip'}`}
          onClick={() => onPlay(clip)}
          disabled={!clipYoutubeId(clip)}
        >
          <span className="w-12 h-12 rounded-full bg-black/70 text-white flex items-center justify-center">
            <FaPlay className="ml-1" />
          </span>
        </button>
      </div>
      <div className="p-3">
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <h3 className={`font-semibold truncate ${archived ? 'text-red-800' : 'text-gray-900'}`}>
              {clip.title || 'Clip'}
            </h3>
            <Link to={videoHref} className="text-sm text-blue-600 underline">
              {formatVideoTime(start)}–{formatVideoTime(end)}
            </Link>
            <p className="text-sm text-gray-600 mt-1">
              {creator ? (
                <Link to={userHref(creator)} className="font-medium text-gray-900 hover:underline">
                  {creator}
                </Link>
              ) : (
                'Creator'
              )}
              {submitter ? (
                <>
                  {' clipped by '}
                  <Link to={userHref(submitter)} className="font-medium text-gray-900 hover:underline">
                    {submitter}
                  </Link>
                </>
              ) : null}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {clip.createdAt ? formatTimeAgo(clip.createdAt) : ''}
              {clip.hallDisplayName ? ` · ${clip.hallDisplayName}` : ''}
            </p>
            {archived && clip.archivedWithVideo && (
              <p className="text-xs text-red-600 mt-1">Archived with its video</p>
            )}
          </div>
          <div className="flex items-center flex-shrink-0">
            {archived && onUnarchive && !clip.archivedWithVideo && (
              <button
                type="button"
                onClick={() => onUnarchive(clip)}
                className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-semibold"
              >
                Unarchive
              </button>
            )}
            {!archived && canRemove && (
              <button
                type="button"
                onClick={() => onRemove(clip)}
                className="text-xs text-white bg-red-600 hover:bg-red-700 rounded p-1.5"
                title="Remove clip"
                aria-label={`Remove ${clip.title || 'clip'}`}
              >
                <FaTrashAlt />
              </button>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

function ClipGrid({ clips, isAppMode, canRemove, onRemove, archived, onUnarchive, onPlay }) {
  const { refFor, isNear } = useNearClipIndexes(clips.length);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {clips.map((clip, index) => (
        <ClipCard
          key={clip.clipId}
          clip={clip}
          isAppMode={isAppMode}
          canRemove={canRemove}
          onRemove={onRemove}
          archived={archived}
          onUnarchive={onUnarchive}
          loadMedia={isNear(index)}
          onPlay={onPlay}
          cardRef={refFor(index)}
        />
      ))}
    </div>
  );
}

export function ProfileClipList({
  clips,
  isAppMode,
  canRemove,
  onRemove,
  showArchived,
  onUnarchive
}) {
  const [playing, setPlaying] = useState(null);
  const [showLandscapeMeta, setShowLandscapeMeta] = useState(false);
  const portrait = useIsPortrait();
  const slotRef = useRef(null);
  const shellRef = useRef(null);
  const shownIdRef = useRef('');
  const poolRef = useRef(new Map());
  const pendingShowRef = useRef(null);
  const playbackRef = useRef({ clipId: null, youtubeId: '', start: 0, end: 0 });
  const enteredRef = useRef(false);
  const wantPlayRef = useRef(false);
  const queueRef = useRef([]);
  const list = Array.isArray(clips) ? [...clips].sort(byNewest) : [];
  const active = list.filter((clip) => !clip.isArchived);
  const archived = list.filter((clip) => clip.isArchived);
  const activeRef = useRef(active);
  activeRef.current = active;
  const preloadId = clipYoutubeId(active.find((clip) => clipYoutubeId(clip)) || {});

  const destroyPool = () => {
    for (const entry of poolRef.current.values()) {
      entry.api.destroy();
      entry.iframe.remove();
    }
    poolRef.current.clear();
    pendingShowRef.current = null;
  };

  const applyVisibility = () => {
    const shown = shownIdRef.current;
    for (const [id, entry] of poolRef.current) {
      const visible = id === shown;
      entry.iframe.style.opacity = visible ? '1' : '0';
      entry.iframe.style.pointerEvents = visible ? 'auto' : 'none';
      entry.iframe.style.zIndex = visible ? '1' : '0';
    }
  };

  const showEntry = (entry) => {
    if (!entry) return;
    shownIdRef.current = entry.youtubeId;
    applyVisibility();
  };

  const ensurePlayer = (youtubeId, clip) => {
    const existing = poolRef.current.get(youtubeId);
    if (existing) return existing;
    const slot = slotRef.current;
    if (!slot || !clip) return null;
    const iframe = document.createElement('iframe');
    iframe.title = clip.title || 'Clip';
    iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;border:0;background:#000;opacity:0;pointer-events:none;z-index:0;';
    slot.appendChild(iframe);
    const api = attachYoutubeEmbed(iframe, {
      onReady: () => {
        const current = playbackRef.current;
        if (current.clipId && current.youtubeId === youtubeId) {
          api.seekTo(current.start, true);
          api.unMute();
          api.playVideo();
          return;
        }
        api.pauseVideo();
      },
      onStateChange: (event) => {
        if (playbackRef.current.youtubeId !== youtubeId) return;
        if (event.data === YT_STATE.ENDED && playbackRef.current.clipId) {
          replayClipFromStart(api, playbackRef.current.start);
          enteredRef.current = false;
          return;
        }
        if (wantPlayRef.current && (event.data === YT_STATE.CUED || event.data === YT_STATE.UNSTARTED)) {
          api.unMute();
          api.playVideo();
        }
      }
    });
    iframe.src = clipPlayerSrc(clip, { autoplay: false });
    const entry = { api, iframe, youtubeId };
    poolRef.current.set(youtubeId, entry);
    return entry;
  };

  const warmNeighbors = (clip) => {
    const queue = queueRef.current;
    const index = queue.findIndex((item) => item.clipId === clip.clipId);
    [queue[index - 1], queue[index + 1]].forEach((neighbor) => {
      const id = clipYoutubeId(neighbor);
      if (id) ensurePlayer(id, neighbor);
    });
    const keep = new Set(
      [queue[index - 1], clip, queue[index + 1]].map((item) => clipYoutubeId(item)).filter(Boolean)
    );
    for (const [id, entry] of poolRef.current) {
      if (keep.has(id)) continue;
      entry.api.destroy();
      entry.iframe.remove();
      poolRef.current.delete(id);
    }
  };

  const closePlayer = () => {
    destroyPool();
    playbackRef.current = { clipId: null, youtubeId: '', start: 0, end: 0 };
    wantPlayRef.current = false;
    exitClipFullscreen();
    setPlaying(null);
  };

  // Sound is started inside the tap or the swipe release. Next and previous
  // players are already loaded, so that request is not waiting on YouTube.
  const playClipNow = (clip, { immediate }) => {
    const youtubeId = clipYoutubeId(clip);
    if (!youtubeId) return;
    const start = clipStartSeconds(clip);
    playbackRef.current = { clipId: clip.clipId, youtubeId, start, end: clipEndSeconds(clip) };
    enteredRef.current = false;
    wantPlayRef.current = true;
    const entry = ensurePlayer(youtubeId, clip);
    if (!entry) return;
    for (const [id, other] of poolRef.current) {
      if (id !== youtubeId) other.api.pauseVideo();
    }
    pendingShowRef.current = null;
    showEntry(entry);
    entry.api.unMute();
    entry.api.seekTo(start, true);
    entry.api.playVideo();
    flushSync(() => setPlaying(clip));
    if (!viewportIsPortrait()) requestClipFullscreen(shellRef.current);
    warmNeighbors(clip);
  };

  const revealPending = () => {
    const entry = pendingShowRef.current;
    if (!entry) return;
    pendingShowRef.current = null;
    showEntry(entry);
  };

  const onTransport = (command) => {
    const current = playbackRef.current;
    const api = poolRef.current.get(current.youtubeId)?.api;
    if (!api || !current.clipId) return;
    if (command === 'toggle') {
      const state = api.getPlayerState();
      if (state === YT_STATE.PLAYING || state === YT_STATE.BUFFERING) {
        wantPlayRef.current = false;
        api.pauseVideo();
      } else {
        wantPlayRef.current = true;
        api.unMute();
        api.playVideo();
      }
      return;
    }
    const nextTime = Math.min(
      current.end - 0.25,
      Math.max(current.start, api.getCurrentTime() + command.seek)
    );
    api.seekTo(nextTime, true);
    api.playVideo();
    enteredRef.current = true;
  };

  const canSwipe = (direction) => !!nextClipInList(
    queueRef.current,
    playbackRef.current.clipId ? { clipId: playbackRef.current.clipId } : null,
    direction
  );

  const openPlayer = (clip) => {
    queueRef.current = active.some((item) => item.clipId === clip.clipId) ? active : archived;
    playClipNow(clip, { immediate: true });
  };

  const swipePlayer = (direction) => {
    const next = nextClipInList(queueRef.current, playbackRef.current.clipId
      ? { clipId: playbackRef.current.clipId }
      : null, direction);
    if (next) playClipNow(next, { immediate: false });
  };

  useEffect(() => {
    if (playing || !preloadId) return undefined;
    const clip = activeRef.current.find((item) => clipYoutubeId(item) === preloadId);
    if (clip) ensurePlayer(preloadId, clip);
    return undefined;
    // The first playable clip warms the player so the opening tap can unmute it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preloadId, playing]);

  useEffect(() => {
    if (!playing) return undefined;
    enteredRef.current = false;
    const id = setInterval(() => {
      const current = playbackRef.current;
      const api = poolRef.current.get(current.youtubeId)?.api;
      if (!api || !current.clipId || api.getPlayerState() !== YT_STATE.PLAYING) return;
      const reported = typeof api.getReportedTime === 'function'
        ? api.getReportedTime()
        : api.getCurrentTime();
      const decision = clipPauseDecision(
        reported,
        current.start,
        current.end,
        enteredRef.current
      );
      enteredRef.current = decision.entered;
      if (decision.pause) {
        replayClipFromStart(api, current.start);
        enteredRef.current = false;
      }
    }, 250);
    return () => clearInterval(id);
  }, [playing]);

  useEffect(() => {
    if (playing && portrait) exitClipFullscreen();
  }, [playing, portrait]);

  useEffect(() => {
    if (!playing || portrait) {
      setShowLandscapeMeta(false);
      return undefined;
    }
    setShowLandscapeMeta(true);
    const timer = setTimeout(() => setShowLandscapeMeta(false), LANDSCAPE_META_MS);
    return () => clearTimeout(timer);
  }, [playing, portrait]);

  return (
    <div>
      {active.length > 0 ? (
        <div className="mb-8">
          <ClipGrid
            clips={active}
            isAppMode={isAppMode}
            canRemove={canRemove}
            onRemove={onRemove}
            onPlay={openPlayer}
          />
        </div>
      ) : (!showArchived || archived.length === 0) ? (
        <p className="text-gray-500 text-center mb-8">No clips yet.</p>
      ) : null}
      {showArchived && archived.length > 0 && (
        <>
          <h2 className="text-xl font-semibold mb-3 text-red-800">Archived Clips</h2>
          <ClipGrid
            clips={archived}
            isAppMode={isAppMode}
            archived
            onUnarchive={onUnarchive}
            onPlay={openPlayer}
          />
        </>
      )}
      <ClipPlayerFrame
        playing={playing}
        slotRef={slotRef}
        shellRef={shellRef}
        portrait={portrait}
        showLandscapeMeta={showLandscapeMeta}
        onClose={closePlayer}
        onSwipe={swipePlayer}
        onCovered={revealPending}
        canSwipe={canSwipe}
        onTransport={onTransport}
      />
    </div>
  );
}
